import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Outlet, useNavigate, useParams}                             from 'react-router-dom'
import {doGet}                                                      from "../../util/do-fetch"
import {getSupabase}                                                from "../../util/supabase-utils"
import {useUserStore}                                               from "../../state"
import {DEBUG, logger}                                                                   from "../../util/log-utils"
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Typography, useMediaQuery, useTheme
} from "@mui/material"
import {ArrowBack, Warning}                  from "@mui/icons-material"
import {findQuestionInPrepKit, scorePrepKit} from "../../util/mini"
import LoadingDots                                                  from "../LoadingDots"
import SelectPackage                                                from "./SelectPackage"
import {cloneDeep}                                                  from "lodash"
import TryPackage                                                   from "./TryPackage"
import {fetchTryPackageUrl, isExpired}                              from "../../util/prepkit-utils"
import ExtendOverlay                                                from "./ExtendOverlay"
import {useCoupon}                                                  from "../../hooks/useCoupon"
import ExpiringPrepKit from "./ExpiringPrepKit"
import ProfileSetupDialog from "./ProfileSetupDialog"

const PrepKitContext = createContext()
const PrepKitBuyContext = createContext()

/*
 * Refetch the prepkit (101).
 *
 * Deliberately a SECOND context rather than a change to PrepKitContext's value: its three
 * consumers read the prepkit itself out of it, and wrapping that in an object would break all of
 * them for no gain.
 *
 * Topic sessions need this. Starting or restarting a topic changes state the realtime `answer`
 * subscription below cannot see — no answer row is written — so nothing else would tell the
 * dashboard that a topic just went from Start to Resume, or that a Restart cleared its answers.
 */
const PrepKitRefreshContext = createContext(async () => {})

const log = logger("PrepKit", DEBUG)

const PrepKit = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const {id, code} = useParams()
	const {user} = useUserStore()
	const coupon = useCoupon()
	const [prepKit, setPrepKit] = useState(null)
	const [packages, setPackages] = useState(null)
	const [trialPackages, setTrialPackages] = useState(null)
	const [justExpired, setJustExpired] = useState(false)
	const [loading, setLoading] = useState(false)
	const [buyNow, setBuyNow] = useState(false)

	/*
	 * Stable so PrepKitBuyContext's value does not change identity on every render of this component
	 * -- an inline arrow would re-render every consumer of the context each time, and the dashboard
	 * is a large one.
	 */
	const openBuy = useCallback(() => setBuyNow(true), [])
	const [error, setError] = useState(null)
	const prepKitRef = useRef(null)
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
	const autoNavRef = useRef(false)
	const prevCodeRef = useRef(code)

	const expired = useMemo(()=>{
		return justExpired || isExpired(prepKit)
	}, [justExpired, prepKit])

	useEffect(() => {
		prepKitRef.current = prepKit;
	}, [prepKit]);

	// subscribe to answer channel
	useEffect(() => {
		if (user && prepKit) {
			const subscribeToAnswerChannel = () => {
				if (!user) return null

				const channelName = `channel-answer-${user.id}`
				console.log(`subscribing to ${channelName}`)
				const supabase = getSupabase()
				return supabase
					.channel(channelName)
					.on(
						"postgres_changes",
						{
							event: "*",
							schema: "public",
							table: "answer",
							filter: `user_id=eq.${user.id}`,
						},
						(event) => {
							log.debug(`${channelName} ${event.eventType} subscribe answer ${event.new.id}`)
							if (event.eventType === "INSERT" ||
								event.eventType === "UPDATE"
							) {
								const newAnswer = event.new

								// Use the ref to access the latest prepKit
								const currentPrepKit = prepKitRef.current;

								if (!currentPrepKit) {
									console.log("no prepKit, bailing");
									return
								}

								if (currentPrepKit.id !== newAnswer.prepkit_id) {
									console.log(`id[${currentPrepKit.id}] !== newId[${newAnswer.prepkit_id}], bailing`);
									return
								}

								setPrepKit(prev => {
									const prepKitX = cloneDeep(prev)

									// update answer array in the prepKit
									const answerIndex = prepKitX.answers.findIndex(a => a.id === newAnswer.id)
									if (answerIndex !== -1) {
										// answer with same id exists
										prepKitX.answers[answerIndex] = newAnswer  // update the answer at the found index
									}
									else {
										// no answer with the same id
										log.debug("did not find matching answer, append to answers")
										prepKitX.answers.push(newAnswer)
									}

									// find the question and update it's answer
									const { question } = findQuestionInPrepKit(prepKitX, newAnswer.question_id)
									question.userAnswer = newAnswer

									// re-score the prepKit to account for new/updated answer
									// NOTE: it is important to note that any objects updated by scoring should have been
									// already cloned above as part of updating the answer state
									scorePrepKit(prepKitX)
									return prepKitX
								})
							}
						}
					)
					.subscribe()
			}

			const answerChannel = subscribeToAnswerChannel();

			return () => {
				if (answerChannel) {
					log.debug("unsubscribe from answerChannel");
					answerChannel.unsubscribe();
				}
			}
		}
	}, [prepKit, user])

	useEffect(() => {
		log.debug(`useEffect[id,prepKit,code] ${id}/${code}`)
		if(id) {
			const fetchPrepKit = async () => {
				if (!prepKit || prepKit.id !== id) {
					setLoading(true)
					setTrialPackages(null)
					setPackages(null)
					// only fetch the prepKit data if not loaded
					const response = await doGet(`/mini/prepkit/${id}`)

					if (response.ok) {
						const data = await response.json()
						scorePrepKit(data) // important to do this before you set local state
						setPrepKit(data)
					}
					else if (response.status === 404) {
						setError("PrepKit not found.")
					}

					setLoading(false)
				}
			}
			fetchPrepKit()
		}
		else if(code) {
			const checkAccess = async () => {
				// get the package/license for this question and user
				try {
					const url = `/mini/check-access/${code}`
					const response = await doGet(url)
					if (response.ok) {
						const data = await response.json()
					    if (data.prepKit) {
						    setTrialPackages(null)
						    setPackages(null)
							navigate(`/prepkit/${data.prepKit.id}/q/${code}`)
						}
						else if (data.trialPackages) {
							// if there are other packages available, then offer them
							setTrialPackages(data.trialPackages)
						}
						else if (data.prepKits) {
							// show modal so user can choose which prepKit
							// this is a RARE possibility
						    console.log('show modal so user can choose which prepKit')
						}
					}
					else if (response.status == 402) { // payment required
						// need to buy a license
						// or renew a license
						const data = await response.json()
						setPackages(data.packages)
					}
					else if (response.status == 404) { // question not found
						// need to buy a license
						// or renew a license
						navigate(`/q?err=${code}`)
					}
				}
				catch (err) {
					log.error(err)
				}
			}
			checkAccess()
		}
	}, [id, prepKit, code, navigate])

	/*
	 * Refetch and rescore the prepkit in place. Shared by the "returning to the dashboard" effect
	 * below and by PrepKitRefreshContext, so both take the identical path.
	 */
	const refresh = useCallback(async () => {
		if (!id) return null

		log.debug(`refresh(${id})`)
		const response = await doGet(`/mini/prepkit/${id}`)
		if (!response.ok) {
			log.error(`refresh failed with status ${response.status}`)
			return null
		}

		const data = await response.json()
		scorePrepKit(data)
		setPrepKit(data)
		return data
	}, [id])

	/*
	 * Refresh progress when returning from a question to the dashboard. The answer-channel
	 * realtime subscription above is meant to keep prepKit live, but if it is not delivering
	 * (e.g. Realtime not enabled for the answer table) the dashboard would show stale progress
	 * until a manual refresh. Refetch on the question -> dashboard transition to guarantee it.
	 *
	 * Topic sessions gave this a second job (101): completed_at moves server-side as the last
	 * question of a topic is answered, and no realtime event carries that.
	 */
	useEffect(() => {
		const wasOnQuestion = Boolean(prevCodeRef.current)
		const nowOnDashboard = !code
		prevCodeRef.current = code

		if (wasOnQuestion && nowOnDashboard && id && prepKit && prepKit.id === id) {
			refresh().catch(err => log.error(`refresh on return failed: ${err.message}`))
		}
	}, [code, id, prepKit, refresh])

	useEffect(() => {
		if (!trialPackages) return
		if (trialPackages.length !== 1) return

		autoNavRef.current = true

		const go = async () => {
			try {
				const url = await fetchTryPackageUrl(trialPackages[0], code)
				navigate(url)
			}
			catch (err) {
				setError(err)
			}
		}

		go()
	}, [trialPackages, code, navigate])

	const handleTryPackage = async (packageToTry) => {
		try {
			const url = await fetchTryPackageUrl(packageToTry, code)
			navigate(url)
		}
		catch (err) {
			log.error(err)
			setError(err)
		}
	}

	const handleSetExpired = () => {
		setJustExpired(true)
	}

	let comp = null

	if(loading) {
		comp = (
			<Box pt={10} display={"flex"} flexDirection={"row"} justifyContent={"center"} alignItems={"center"}>
				<CircularProgress color="secondary" size={18}  sx={{mr: 1}}/>
				<Typography variant={isSmallScreen ? "h6" : "h6"} color={"text.disabled"}>
					Loading PrepKit
				</Typography>
			</Box>
		)
	}
	else if(error) {
		comp = (
			<Card elevation={2} sx={{width: "600px", p: 2}} margin={"0 auto"}>
				<CardContent>
					<Box display={"flex"} flexDirection={"row"} justifyContent={"flex-start"} alignItems={"center"}>
						<Warning sx={{mr: 1, color: theme.palette.warning.main}}/>
						<Typography variant={"h6"}>{error}</Typography>
					</Box>
				</CardContent>
				<CardActions>
					<Button onClick={()=>navigate('/prepkit')}>
						<ArrowBack/> Back to PrepKits
					</Button>
				</CardActions>
			</Card>
		)
	}
	else if(packages) {
		comp = (
			<Box textAlign="center">
				{/* we may need to show available packages because user may have  */}
				<SelectPackage title="Purchase PrepKit"
				                 packages={packages}
				                 onCancel={()=>{
					                 navigate('..')
				                 }}
				                 onSelectPackage={(p)=>navigate(`/prep/${p.category.token}/${p.token}`)}
				/>
			</Box>
		)
	}
	else if(trialPackages) {
		comp = (
			<Box textAlign="center">
				<TryPackage packages={trialPackages}
				            onTryPackage={handleTryPackage}
				            onCancel={()=>{
					            navigate('/prep')
				            }}
				/>
			</Box>
		)
	}
	else {
		comp = <Outlet />
	}

	return (
		<PrepKitContext.Provider value={prepKit}>
			<PrepKitRefreshContext.Provider value={refresh}>
			{/*
			  * A THIRD context rather than a field on PrepKitContext (169), for the same reason
			  * written above about refresh: three consumers read the prepkit straight out of that
			  * context, and wrapping its value in an object to carry a second field would break all
			  * of them. This one carries "open the buy overlay", which the dashboard's trial banner
			  * needs and which only this component can do.
			  */}
			<PrepKitBuyContext.Provider value={openBuy}>
			<Box sx={{border: "0px solid red"}}>
				{/*
				  * Before {comp}, so the countdown is in the document flow ABOVE the page rather than
				  * fixed over it (168). It stays HERE rather than moving inside the dashboard: comp is
				  * an <Outlet/>, so this one mount covers the dashboard and the question screen, and
				  * the question screen is where a trial user actually spends the countdown.
				  */}
				<ExpiringPrepKit prepKit={prepKit} onBuy={openBuy} onPrepKitExpired={handleSetExpired}/>
				{/*
				  * First-run setup (196). Mounted HERE rather than on the dashboard: this component
				  * wraps the dashboard and every study screen through its <Outlet/>, so a student who
				  * lands straight on a question screen is asked too. It decides for itself whether
				  * there is anything to ask.
				  */}
				<ProfileSetupDialog/>
				{comp}
				<Box sx={{border: "0px solid orange"}}>
					<ExtendOverlay open={expired || buyNow}
					               prepKit={prepKit}
					               coupon={coupon}
					               variant={buyNow ? 'buy' : 'expired'}
					               onCancelBuy={()=>setBuyNow(false)}
					/>
				</Box>
			</Box>
			</PrepKitBuyContext.Provider>
			</PrepKitRefreshContext.Provider>
		</PrepKitContext.Provider>
	)
}

export default PrepKit
export {PrepKitContext, PrepKitRefreshContext, PrepKitBuyContext}
